// ANGULAR
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Title } from '@angular/platform-browser';
// SERVICES
import { AlertService } from '@services/alert.service';
import { GlobalService } from '@services/global.service';
import { ConnectionStringsService } from '@api-requests-services/connection-strings.service';
// MODELS
import { ConnectionString } from '@models/connection-strings.model';

export enum Views {
  Home = 'home',
  ConnectionStrings = 'connectionStrings',
  CreatingConnectionString = 'creatingConnectionString',
  EditingConnectionString = 'editingConnectionString',
}

@Component({
  selector: 'app-connection-strings',
  templateUrl: './connection-strings.component.html',
  styleUrl: './connection-strings.component.css',
  imports: [
    CommonModule,
    FormsModule
  ]
})
export class ConnectionStringsComponent {
  views = Views;
  currentView: string = Views.Home;
  allConnectionStrings: ConnectionString[];
  connectionStrings: ConnectionString[];
  name: string;
  value: string;
  connectionStringSearched: ConnectionString | null;
  connectionStringNameToSearch: string;
  connectionStringToSearch: string;
  connectionStringNotFound: boolean = false;
  connectionStringSelected: ConnectionString | null = null;

  constructor(
    private alertService: AlertService,
    public globalService: GlobalService,
    private connectionStringsService: ConnectionStringsService,
    private title: Title
  ) {
    this.title.setTitle('Oráculo - Connection Strings');
  }

  getConnectionStrings(): void {
    this.connectionStringsService.getConnectionStrings().subscribe(
      connectionStrings => {
        if (connectionStrings) {
          // console.log('ConnectionStrings: ', connectionStrings); 
          this.allConnectionStrings = connectionStrings;
          this.connectionStrings = this.allConnectionStrings;
        }
      },
      error => {
        if (error.error === this.globalService.editModeDisabledMsg) {
          this.alertService.swalEditingNotEnabled();
        } else {
          this.alertService.swalError(error.error);
          console.error('Error getting Connection Strings:', error);
        }
      }
    );
  }

  createConnectionString(): void {
    const connectionStrings: ConnectionString = {
      nombre: this.name,
      valor: this.value
    };

    this.connectionStringsService.postCreateConnectionString(connectionStrings).subscribe(
      () => {
        this.selectView('connectionStrings');
        this.alertService.showAutoCloseMessage(`¡Cadena de conexión creada!`, ``, "success", 1200);
      },
      error => {
        if (error.error === this.globalService.editModeDisabledMsg) {
          this.alertService.swalEditingNotEnabled();
        } else {
          this.alertService.swalError(error.error);
          console.error('Error creating Connection String:', error);
        }
      }
    );
  }

  getConnectionString(): void {
    if (this.connectionStringNameToSearch) {
      this.connectionStringsService.getConnectionString(this.connectionStringNameToSearch).subscribe(
        connectionString => {
          if (connectionString) {
            // console.log('ConnectionString: ', connectionStrings);
            this.connectionStringNotFound = false;
            this.connectionStringSearched = connectionString;
            this.allConnectionStrings = [connectionString];
          }
        },
        error => {
          if (error.error === this.globalService.editModeDisabledMsg) {
            this.alertService.swalEditingNotEnabled();
          } else {
            if (error.status === 404) {
              this.connectionStringNotFound = true;
            } else {
              this.connectionStringNotFound = false;
              this.alertService.swalError(error.error);
            }
            console.error('Error getting Connection String:', error);
          }
        }
      );
    } else {
      this.connectionStringNotFound = true;
    }
  }

  editConnectionString(): void {
    const connectionString: ConnectionString = {
      nombre: this.name,
      valor: this.value
    };

    this.alertService.showConfirmAlert(
      `Editar Cadena de conexión`,
      `¿Seguro que quieres editar la Cadena de conexión con nombre "${this.connectionStringSelected?.nombre ?? ''}"?`
    ).then((result) => {
      if (result.isConfirmed && !!this.connectionStringSelected) {
        this.connectionStringsService.putEditConnectionString(this.connectionStringSelected.nombre, connectionString).subscribe(
          () => {
            this.alertService.showAutoCloseMessage(`¡Cadena de conexión editada!`, ``, "success", 1200);
            this.selectView('connectionStrings');
          },
          error => {
            if (error.error === this.globalService.editModeDisabledMsg) {
              this.alertService.swalEditingNotEnabled();
            } else {
              this.alertService.swalError(error.error);
              console.error('Error editing Connection String:', error);
            }
          }
        );
      }
    });
  }

  deleteConnectionString(connectionString: ConnectionString): void {
    this.alertService.showConfirmAlert(
      `Eliminar Cadena de conexión`,
      `¿Seguro que quieres eliminar la Cadena de conexión con nombre "${connectionString.nombre}"?`
    ).then((result) => {
      if (result.isConfirmed && connectionString.nombre) {
        this.connectionStringsService.deleteConnectionString(connectionString.nombre).subscribe(
          () => {
            this.alertService.showAutoCloseMessage(`¡Cadena de conexión borrada!`, ``, "success", 1200);
            this.getConnectionStrings();
          },
          error => {
            if (error.error === this.globalService.editModeDisabledMsg) {
              this.alertService.swalEditingNotEnabled();
            } else {
              this.alertService.swalError(error.error);
              console.error('Error deleting Connection String:', error);
            }
          }
        );
      }
    });
  }

  selectView(view: string, connectionStringSelected?: ConnectionString): void {
    if (!!connectionStringSelected) { this.connectionStringSelected = connectionStringSelected; }
    this.currentView = view;

    switch (this.currentView) {
      case Views.Home:
        this.connectionStringSearched = null;
        this.connectionStringNameToSearch = '';
        this.name = '';
        this.value = '';
        this.connectionStringToSearch = '';
        this.connectionStrings = [];
        break;
      case Views.ConnectionStrings:
        this.getConnectionStrings();
        this.name = '';
        this.value = '';
        break;
      case Views.CreatingConnectionString:
        this.name = '';
        this.value = '';
        break;
      case Views.EditingConnectionString:
        this.connectionStringToSearch = '';
        this.name = !!this.connectionStringSelected ? this.connectionStringSelected.nombre : '';
        this.value = !!this.connectionStringSelected ? this.connectionStringSelected.valor : '';
        break;
    }
  }

  onSearchChange(): void {
    this.connectionStrings = this.allConnectionStrings.filter(connectionString => connectionString.nombre.toLowerCase().includes(this.connectionStringToSearch.toLowerCase()));

    if (this.connectionStringToSearch === undefined || this.connectionStringToSearch === '' || this.connectionStringToSearch === ' ') {
      this.connectionStrings = this.allConnectionStrings;
    }
  }
}